/**
 * Video text -> coded observation.
 *
 * The overlay text an inspection van burns into the video already carries the EN 13508-2 /
 * ISYBAU coding of the observation. This turns one such line into the coded fields the XML
 * export needs: main code, C1, C2, Q1, Q2 and the two clock positions.
 *
 * Why a second implementation when the OCR pipeline already fills these: that one is an NLU
 * model that runs once, in the batch job, and only for detections it found itself. A reviewer
 * adding a damage by hand has the overlay text in front of them and nothing to fill the coded
 * fields from. Rules also fail visibly here -- a field stays empty -- rather than guessing.
 *
 * The output shape follows the Python pipeline (AI-driven-Inspection-Insights,
 * DwaCodeInfoExtractor.py) so that a reviewer-added row and a model-produced row are directly
 * comparable:
 *
 *   - Q1/Q2 hold the NUMBER, without the label or the unit around it: "2", not
 *     "Querschnittsreduzierung = 2%". Q1 and Q2 are quantifiers -- in EN 13508-2 and in the
 *     ISYBAU/DWA documents they are numeric fields -- so the label is not part of the value,
 *     and which quantity a code's Q1 means is fixed by the code, not by the word the van
 *     happened to print. Keeping the phrase put "Querschnittsreduzierung" in a number field
 *     and left a reviewer-entered row unable to match its OCR counterpart on that field.
 *
 *     The reference detections in the Python pipeline DO carry the phrase, and so does the
 *     OCR output this app reads. Nothing downstream is affected: both exporters reduce the
 *     field to its first number anyway (ValueCleaner.ExtractFirstNumber on the .NET side, a
 *     regex on the Python side), and neither decides the target element from the wording.
 *
 *   - SA is never set. It is the DWA-M 149-2 "Standardisierte Anmerkung" (DWA field HZ017),
 *     a code-specific annotation that ISYBAU routes into GrundAbbruch, BAKZustandSanierung,
 *     BALZustandSanierung or RVerfahrenSanierung depending on the main code. It is NOT the
 *     start/end marker of a stretch damage, and the model leaves it empty for every line
 *     these rules can read, so this leaves it empty too. Writing an A/Z/E there put a stretch
 *     marker into a rehabilitation field on BCB/BAK/BAL/BDC observations.
 *
 * The three shapes real vendor overlays come in (all sampled from OCR output):
 *   code first   "BCAAA 150.00mm 150.00mm (11Uhr) Bestandsaufnahme Anschluss Abzweig ..."
 *   Pos-prefixed "Pos: 9 - 3; Wurzeln, einzelne, feine Wurzeln, Querschnittsreduzierung = 2%"
 *   German prose "Anschluss: Abzweig, offen, 150mm hoch, 150mm breit, bei 10 Uhr"
 */

/** Field names match EditableDetection, so a result can be spread straight onto the form. */
export interface ParsedVideoText {
  main_Code: string;
  c1: string;
  c2: string;
  q1: string;
  q2: string;
  postionFrom: string;
  positionTo: string;
}

export interface ParseVideoTextOptions {
  /**
   * Main codes to accept from the head of the line. OCR drops and invents letters
   * ("CAAA150.00mm", "BCI 150.00mm"), and a head token that is not a real code is worse than
   * no code at all. Only the head token is checked against this -- the phrase table below
   * carries its own codes and is trusted. Omit or pass an empty list to accept any
   * well-formed token.
   */
  knownCodes?: string[];
  /** Set false to leave the main code empty when the line has no code prefix. */
  usePhraseFallback?: boolean;
}

/**
 * A three-letter code, then up to three more letters of characterisation. The trailing \b is
 * what keeps German words out: "ANFANGSKNOTEN, ROHRANFANG" has no boundary after any 3-6
 * letter prefix, so it never reads as a code.
 */
const HEAD_CODE = /^\s*([A-D][A-Z]{2})([A-Z]{0,3})\b/;

/**
 * Main codes for lines that spell the damage out in German instead of coding it. Specific
 * before generic -- "schadhafter Anschluss" must not be read as a plain "Anschluss".
 *
 * C1/C2 are carried only where the Python reference output pins them down for that exact
 * wording (the sample detections in isybau_xml_builder.py). Where the characterisation turns
 * on a word the table can actually judge -- "Laengsrichtung", "offen" -- the specific row
 * requires that word and a generic row without it catches the rest, so an unseen wording
 * gets an empty field rather than a wrong one.
 */
const PHRASE_CODES: Array<{ re: RegExp; main: string; c1?: string; c2?: string }> = [
  // ORDER IS THE LOGIC: first match wins, so a pattern that pins a characterisation must sit
  // above the one that pins only its main code, and a C1+C2 pattern above its C1-only sibling.
  // "Klaffender Riss" contains "Riss", so all three Klaffender rules precede the plain ones.
  // Every wording in OBSERVATION_WORDING has a rule here that reads it back to the same three
  // codes -- observationRoundTrip.test.ts asserts exactly that, entry by entry.

  // Nodes ------------------------------------------------------------------------------------
  { re: /\b(?:Rohranfang|Anfangsknoten)\b/i, main: "BCD", c1: "XP" },
  { re: /\b(?:Rohrende|Endknoten)\b/i, main: "BCE", c1: "XP" },

  // Cracks: C1 is the crack, C2 its course --------------------------------------------------
  { re: /\bklaffender\s+Riss\b.*\bL[\u00e4a]ngs/i, main: "BAB", c1: "C", c2: "A" },
  { re: /\bklaffender\s+Riss\b.*\bRohrumfang\b/i, main: "BAB", c1: "C", c2: "B" },
  { re: /\bklaffender\s+Riss\b.*\bscherbenf[\u00f6o]rmig\b/i, main: "BAB", c1: "C", c2: "C" },
  { re: /\bklaffender\s+Riss\b/i, main: "BAB", c1: "C" },
  { re: /\bOberfl[\u00e4a]chenriss\b.*\bVerlauf\s+unklar\b/i, main: "BAB", c1: "A", c2: "Y" },
  { re: /\bOberfl[\u00e4a]chenriss\b.*\bL[\u00e4a]ngs/i, main: "BAB", c1: "A", c2: "A" },
  { re: /\bOberfl[\u00e4a]chenriss\b.*\bRohrumfang\b/i, main: "BAB", c1: "A", c2: "B" },
  { re: /\bOberfl[\u00e4a]chenriss\b.*\bscherbenf[\u00f6o]rmig\b/i, main: "BAB", c1: "A", c2: "C" },
  { re: /\b(?:Oberfl[\u00e4a]chenriss|Haarriss)\b/i, main: "BAB", c1: "A" },
  { re: /\bRiss\b.*\bL[\u00e4a]ngs/i, main: "BAB", c1: "B", c2: "A" },
  { re: /\bRiss\b.*\bRohrumfang\b/i, main: "BAB", c1: "B", c2: "B" },
  { re: /\bRiss\b.*\bscherbenf[\u00f6o]rmig\b/i, main: "BAB", c1: "B", c2: "C" },
  { re: /\bRiss(?:bildung)?\b/i, main: "BAB", c1: "B" },

  // Everything else -------------------------------------------------------------------------
  { re: /\bSpalt\s+am\s+Anschlussumfang\b/i, main: "BAH", c1: "C" },
  { re: /\bschadhafter\s+Anschluss\b/i, main: "BAH", c1: "C" },
  { re: /\beinragende(?:r|s)\s+\w+/i, main: "BAG" },
  { re: /\bverschobene\s+(?:Rohr)?verbindung\b.*\bl[\u00e4a]ngs\b/i, main: "BAJ", c1: "A" },
  { re: /\bverschobene\s+(?:Rohr)?verbindung\b.*\bradial\b/i, main: "BAJ", c1: "B" },
  { re: /\bverschobene\s+(?:Rohr)?verbindung\b.*\babgewinkelt\b/i, main: "BAJ", c1: "C" },
  { re: /\bverschobene\s+(?:Rohr)?verbindung\b/i, main: "BAJ", c1: "B" },
  { re: /\bVerformung\b.*\bvertikal\b/i, main: "BAA", c1: "A" },
  { re: /\bVerformung\b/i, main: "BAA" },
  { re: /\bAbplatzung(?:en)?\b/i, main: "BAF", c1: "B" },
  { re: /\bOberfl[\u00e4a]chenschaden\b/i, main: "BAF", c1: "B" },
  { re: /\b(?:Rohrbruch|Einsturz|Bruch)\b/i, main: "BAC", c1: "A" },
  { re: /\bWurzeln\b.*\bkomplex/i, main: "BBA", c1: "C" },
  { re: /\bWurzeln\b/i, main: "BBA", c1: "B" },
  { re: /\bInkrustation\b/i, main: "BBB", c1: "A" },
  { re: /\bAnhaftende\s+Stoffe\b/i, main: "BBB" },
  { re: /\bAblagerungen?\b/i, main: "BBC" },
  { re: /\bBoden\s+sichtbar\b/i, main: "BAO" },
  { re: /\bWasserspiegel\b/i, main: "BDD" },
  { re: /\bPunktuelle\s+Reparatur\b/i, main: "BCB" },
  { re: /\bKr[\u00fcu]mmung\b/i, main: "BCC" },
  // Connections: C1 is how it was made, C2 whether it is open -------------------------------
  { re: /\bAbzweig\b.*\bvorgefertigt/i, main: "BCA", c1: "A", c2: "A" },
  { re: /\bAbzweig\b.*\bgeschlossen\b/i, main: "BCA", c1: "A", c2: "B" },
  { re: /\bgebohrt\b.*\bgeschlossen\b/i, main: "BCA", c1: "D", c2: "B" },
  { re: /\bgebohrt\b/i, main: "BCA", c1: "D", c2: "A" },
  { re: /\bandere\s+Art\b.*\bgeschlossen\b/i, main: "BCA", c1: "Z", c2: "B" },
  { re: /\bandere\s+Art\b/i, main: "BCA", c1: "Z", c2: "A" },
  // C1 E is identified by "ohne Formstück" / "gemeißelt". NOT by "einfacher Anschluss", which
  // C1 D's wording also carries -- keying on that phrase sent every gebohrt row to E.
  { re: /\bohne\s+Formst[\u00fcu]ck\b/i, main: "BCA", c1: "E", c2: "A" },
  { re: /\bgemei[\u00dfs]elt\b/i, main: "BCA", c1: "E", c2: "A" },
  // The same connection as read off a van's overlay, where the sharp s rarely survives OCR
  // ("g?mei?elt"). These two match the words that do survive, and they sit BELOW the gebohrt
  // rules so a drilled connection is claimed before "einfacher Anschluss" can speak for it.
  { re: /\beinfacher\s+Anschluss\b.*\boffen\b/i, main: "BCA", c1: "E", c2: "A" },
  { re: /\beinfacher\s+Anschluss\b/i, main: "BCA", c1: "E" },
  { re: /\b(?:Anschluss|Abzweig)\b/i, main: "BCA" },
];

/**
 * Clock positions, most explicit wording first. `pair` marks the patterns that carry both
 * ends of a range.
 */
const POSITION_PATTERNS: Array<{ re: RegExp; pair?: boolean }> = [
  { re: /\bvon\s*(\d{1,2})\s*bis\s*(\d{1,2})\s*Uhr\b/i, pair: true },
  // "jbei 1 Uhr" is real: OCR glues a stray letter onto "bei".
  { re: /\b\w?bei\s*(\d{1,2})\s*Uhr\b/i },
  { re: /\bPos\.?:?\s*(\d{1,2})\s*[-\u2013]\s*(\d{1,2})/i, pair: true },
  { re: /\bPos\.?:?\s*(\d{1,2})/i },
  { re: /\(\s*(\d{1,2})\s*[-\u2013]\s*(\d{1,2})\s*(?:Uhr)?/i, pair: true },
  { re: /\(?\s*(\d{1,2})\s*Uhr\b/i },
  // Bare "(10)", how one vendor writes a single position. Last, because it is the pattern
  // most easily confused with a number that means something else.
  { re: /\(\s*(\d{1,2})\s*\)/ },
];

/**
 * Drops the leading "(A) Anfang (1):" / "(B) Ende (1):" marker of a stretch damage. Its
 * sequence number is in parentheses and would otherwise read as a clock position.
 */
const STRETCH_MARKER = /^\s*\([AB]\)\s*(?:Anfang|Ende)\s*\(\d+\)\s*:?/i;

/**
 * Quantities. The keyword form comes first and swallows its own unit, so the "00mm" left over
 * from "Hoehe = 150,00mm" cannot be picked up as a second measurement.
 *
 * A unit (or a keyword) is required throughout: without that rule the node name in
 * "Rohrende 2283030010" reads as a quantity.
 */
const QUANTITY = new RegExp(
  [
    "(?:H(?:\u00f6he|ohe)?|B(?:reite)?|L(?:\u00e4nge|ange)?|Dist(?:anz)?\\.?" +
      "|Querschnitts(?:reduzierung|verminderung))\\s*[=:]\\s*(\\d{1,4}(?:[.,]\\d{1,3})?)" +
      "\\s*(?:mm|cm|%|\u00b0|grad)?",
    "(\\d{1,4}(?:[.,]\\d{1,3})?)\\s*(?:mm|cm|%|\u00b0|\u00ba|grad\\b|\\*)",
    "(\\d{1,4}(?:[.,]\\d{1,3})?)\\s*Richtungs[\u00e4a]nderung",
  ].join("|"),
  "gi"
);

/** A clock position, or "" for anything outside 1-12 (OCR reads "16 Uhr" often enough). */
const clock = (raw: string | undefined): string => {
  if (!raw) return "";
  const n = Number(raw);
  return Number.isInteger(n) && n >= 1 && n <= 12 ? String(n) : "";
};

const codeOf = (
  text: string,
  knownCodes: string[] | undefined,
  usePhraseFallback: boolean
): { main: string; c1: string; c2: string } | null => {
  const head = HEAD_CODE.exec(text);
  if (head) {
    const main = head[1];
    const rest = head[2];
    if (!knownCodes || knownCodes.length === 0 || knownCodes.indexOf(main) >= 0) {
      // XP is one two-letter characterisation, not C1 followed by C2.
      if (rest === "XP") return { main, c1: "XP", c2: "" };
      return { main, c1: rest.slice(0, 1), c2: rest.slice(1, 2) };
    }
  }

  if (!usePhraseFallback) return null;

  for (const phrase of PHRASE_CODES) {
    if (phrase.re.test(text)) {
      return { main: phrase.main, c1: phrase.c1 ?? "", c2: phrase.c2 ?? "" };
    }
  }
  return null;
};

const positionsOf = (raw: string): { from: string; to: string } | null => {
  // Damage around the whole circumference is written as 12 to 12.
  if (/Gesamtumf|Vollumf/i.test(raw)) return { from: "12", to: "12" };

  const text = raw.replace(STRETCH_MARKER, "");
  for (const pattern of POSITION_PATTERNS) {
    const m = pattern.re.exec(text);
    if (!m) continue;
    const from = clock(m[1]);
    if (!from) continue;
    return { from, to: pattern.pair ? clock(m[2]) : "" };
  }
  return null;
};

/**
 * Q1 and Q2 in reading order, which is the order a code's own quantities are written in.
 *
 * Only the number is kept -- "150,00" from "Hoehe = 150,00mm", "2" from
 * "Querschnittsreduzierung = 2%". The label and the unit are what the van printed, not the
 * value of the field; the code already says which quantity its Q1 is.
 *
 * The whole match still drives how far the scan advances, so a keyword form swallows its own
 * unit and the "00mm" left over from "Hoehe = 150,00mm" cannot come back as a second quantity.
 */
const quantitiesOf = (text: string): string[] => {
  const found: string[] = [];
  QUANTITY.lastIndex = 0;
  let m = QUANTITY.exec(text);
  while (m !== null && found.length < 2) {
    // One of the three alternatives captured a number; without that the match is noise.
    const number = m[1] ?? m[2] ?? m[3];
    if (number) found.push(number.trim());
    m = QUANTITY.exec(text);
  }
  return found;
};

/**
 * The coded fields this text actually spells out. Fields the text is silent about are left
 * out of the result entirely, so it can be spread onto a partly filled form without wiping
 * anything the reviewer already entered.
 */
export const parseVideoText = (
  text: string,
  options: ParseVideoTextOptions = {}
): Partial<ParsedVideoText> => {
  const parsed: Partial<ParsedVideoText> = {};
  if (!text || !text.trim()) return parsed;

  const code = codeOf(text, options.knownCodes, options.usePhraseFallback !== false);
  if (code) {
    parsed.main_Code = code.main;
    if (code.c1) parsed.c1 = code.c1;
    if (code.c2) parsed.c2 = code.c2;
  }

  const [q1, q2] = quantitiesOf(text);
  if (q1) parsed.q1 = q1;
  if (q2) parsed.q2 = q2;

  const positions = positionsOf(text);
  if (positions) {
    parsed.postionFrom = positions.from;
    if (positions.to) parsed.positionTo = positions.to;
  }

  return parsed;
};
